import React, { useState } from "react";
import { useCity, useDeleteCity, useEditCity } from "../../hooks/useCity";
import { usePermission } from "../../hooks/usePermission";
import {
  DeleteCitiesPermission,
  EditCitiesPermission,
} from "../../constants/permissions";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Menu,
  MenuItem,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useCountry, useCountryNameById } from "../../hooks/useCountry";
import DataTable from "../shared/DataTable";

const CountryNameCell = ({ countryId }) => {
  const { data: countryName } = useCountryNameById(countryId);
  return countryName || "";
};

function CitiesTable() {
  const { data: citiesData, isLoading } = useCity();
  const { data: countriesData } = useCountry();
  const { mutate: deleteCity } = useDeleteCity();
  const { mutate: editCity } = useEditCity();

  const { data: canEditCity } = usePermission({
    permissionName: EditCitiesPermission,
  });

  const { data: canDeleteCity } = usePermission({
    permissionName: DeleteCitiesPermission,
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedActionId, setSelectedActionId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: "",
    country_id: "",
  });
  const [selectedCity, setSelectedCity] = useState(null);

  const handleMenuClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedActionId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedActionId(null);
  };

  const handleEditClick = (city) => {
    setSelectedCity(city);
    setEditFormData({
      name: city.name,
      country_id: city.country_id,
    });
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedCity(null);
  };

  const handleEditSubmit = async () => {
    try {
      editCity({
        id: selectedCity.id,
        data: editFormData,
      });
      handleEditClose();
    } catch (error) {
      console.error("Error editing city:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this city?")) {
      try {
        deleteCity(id);
        handleMenuClose();
      } catch (error) {
        console.error("Error deleting city:", error);
      }
    }
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Country Name",
      selector: (row) => {
        return <CountryNameCell countryId={row.country_id} />;
      },
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <IconButton onClick={(e) => handleMenuClick(e, row.id)}>
          <MoreVertIcon />
        </IconButton>
      ),
      width: "200px",
    },
  ];

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <DataTable
        columns={columns}
        data={citiesData}
        searchFields={["name"]}
        searchPlaceholder="Search cities..."
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {canEditCity && (
          <MenuItem
            onClick={() =>
              handleEditClick(
                citiesData.find((st) => st.id === selectedActionId)
              )
            }
          >
            <i className="fa-solid fa-edit text-green-700 mr-2 mb-1" />
            <h6 className="text-blueColor">Edit</h6>
          </MenuItem>
        )}
        {canDeleteCity && (
          <MenuItem onClick={() => handleDelete(selectedActionId)}>
            <i className="fa-solid fa-trash text-red-600 mr-2" />
            <h6 className="text-blueColor">Delete</h6>
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="bg-blueColor text-white py-4">
          <i className="fas fa-edit text-xl mx-2"></i>
          <span className="text-xl font-semibold">Edit City</span>
        </DialogTitle>
        <DialogContent className="mt-4">
          <div className="grid grid-cols-1 gap-4 p-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Country Name
              </label>
              <select
                name="country_id"
                value={editFormData.country_id}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    country_id: e.target.value,
                  })
                }
                className="w-full p-2.5 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              >
                <option value="" hidden>
                  Select a country
                </option>
                {countriesData?.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                City
              </label>
              <input
                type="text"
                name="name"
                value={editFormData.name}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    name: e.target.value,
                  })
                }
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                disabled={!editFormData.country_id} // disable input if no country is selected
              />
            </div>
          </div>

          <div className="flex justify-end gap-4 mt-6 p-4">
            <button
              onClick={handleEditClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
            >
              Cancel
            </button>
            <button
              onClick={handleEditSubmit}
              className="px-4 py-2 bg-blueColor text-white rounded"
            >
              Save
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CitiesTable;
